import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import UpgradeCheckoutForm from "./UpgradeCheckoutForm";
import api from "../../utils/api";
import Stepper from '../../assets/components/Stepper';
import { ShieldCheck, CheckCircle } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const stripePromise = loadStripe('pk_test_51QGQTkCtf3ESElJ3xz7bgjmmmFde6W8qUOw4F1UuMT1vgfK5QR9jzNgsSERRDEWHMVZ936yhDBWwsypUtAa6zh5K00TCbWa5bm');

const UpgradeCheckoutPage = () => {
    const { planId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [clientSecret, setClientSecret] = useState(null);
    const [planDetails, setPlanDetails] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [currentUserPlan, setCurrentUserPlan] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        // Verificar que el usuario esté autenticado
        if (!user || !user._id) {
            console.warn("User not authenticated. Redirecting to login.");
            navigate('/login');
            return;
        }

        const storedPlanId = sessionStorage.getItem('selectedPlanId');
        const upgradePlanData = sessionStorage.getItem('upgradePlanData');

        console.log("SessionStorage data:", { storedPlanId, upgradePlanData });

        // Verificar si es un upgrade válido
        if (!storedPlanId || !upgradePlanData) {
            console.warn("Missing upgrade data. Redirecting to plans.");
            navigate('/services-plans');
            return;
        }

        try {
            const upgradeData = JSON.parse(upgradePlanData);
            if (!upgradeData.isUpgrade) {
                navigate('/services-plans');
                return;
            }
        } catch (error) {
            console.error("Error parsing upgrade data:", error);
            navigate('/services-plans');
            return;
        }

        const createPaymentIntent = async () => {
            try {
                // Obtener detalles del plan seleccionado
                const planResponse = await api.get(`/plan/${storedPlanId}`);
                if (!planResponse.data.success) throw new Error("Could not fetch plan details.");

                const plan = planResponse.data.content;
                setPlanDetails(plan);

                // Obtener el plan actual del usuario usando el endpoint correcto
                try {
                    const businessResponse = await api.get(`/business/by_user/${user._id}`);
                    if (businessResponse.data.success && businessResponse.data.content?.length > 0) {
                        const business = businessResponse.data.content[0];
                        if (business.plan_id) {
                            const currentPlanResponse = await api.get(`/plan/${business.plan_id}`);
                            if (currentPlanResponse.data.success) {
                                setCurrentUserPlan(currentPlanResponse.data.content);
                            }
                        }
                    }
                } catch (businessError) {
                    console.warn("Could not fetch current user plan:", businessError);
                    // No es crítico, continuamos sin mostrar el plan actual
                }

                // Crear el payment intent para el upgrade
                const amountInCents = Math.round(plan.price * 100);
                console.log("Creating payment intent for plan:", plan._id, "amount:", amountInCents);
                console.log("User ID:", user._id);
                
                const paymentIntentResponse = await api.post("/stripe/create-payment-intent", {
                    amount: amountInCents,
                    currency: "usd",
                    plan_id: plan._id,
                    isUpgrade: true,
                    user_id: user._id
                });
                
                console.log("Payment intent response:", paymentIntentResponse.data);
                
                // Manejar la respuesta del payment intent (incluso si hay duplicación de keys)
                const responseData = paymentIntentResponse.data;
                if (responseData.clientSecret) {
                    // Si hay múltiples clientSecret, usar el último (más completo)
                    const clientSecrets = Array.isArray(responseData.clientSecret) 
                        ? responseData.clientSecret 
                        : [responseData.clientSecret];
                    
                    // Encontrar el clientSecret más largo (más completo)
                    const longestClientSecret = clientSecrets.reduce((longest, current) => 
                        current.length > longest.length ? current : longest
                    );
                    
                    setClientSecret(longestClientSecret);
                    console.log("Payment intent created successfully with client secret length:", longestClientSecret.length);
                } else {
                    throw new Error("No client secret received from payment intent");
                }
            } catch (err) {
                console.error("Error setting up upgrade payment:", err);
                setError(err.message || "Failed to setup payment. Please try again.");
                // No redirigir automáticamente, mostrar el error al usuario
            } finally {
                setIsLoading(false);
            }
        };

        createPaymentIntent();
    }, [navigate, user]);

    if (isLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex justify-center items-center">
                <div className="text-center">
                    <svg className="animate-spin h-8 w-8 text-blue-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <p className="text-gray-700 text-lg mt-4">Preparing secure payment for upgrade...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex justify-center items-center">
                <div className="text-center max-w-md mx-auto px-4">
                    <div className="bg-white rounded-lg shadow-lg p-8">
                        <div className="text-red-500 text-6xl mb-4">⚠️</div>
                        <h2 className="text-2xl font-bold text-gray-800 mb-4">Payment Setup Error</h2>
                        <p className="text-gray-600 mb-6">{error}</p>
                        <div className="space-y-3">
                            <button 
                                onClick={() => window.location.reload()} 
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-md"
                            >
                                Try Again
                            </button>
                            <button 
                                onClick={() => navigate('/services-plans')} 
                                className="w-full bg-gray-500 hover:bg-gray-600 text-white font-semibold py-3 px-4 rounded-md"
                            >
                                Back to Plans
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!clientSecret) {
        return (
            <div className="min-h-screen bg-gray-50 flex justify-center items-center">
                <div className="text-center max-w-md mx-auto px-4">
                    <div className="bg-white rounded-lg shadow-lg p-8">
                        <div className="text-yellow-500 text-6xl mb-4">⏳</div>
                        <h2 className="text-2xl font-bold text-gray-800 mb-4">Payment Not Ready</h2>
                        <p className="text-gray-600 mb-6">The payment system is not ready yet. Please wait a moment or try again.</p>
                        <div className="space-y-3">
                            <button 
                                onClick={() => window.location.reload()} 
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-md"
                            >
                                Try Again
                            </button>
                            <button 
                                onClick={() => navigate('/services-plans')} 
                                className="w-full bg-gray-500 hover:bg-gray-600 text-white font-semibold py-3 px-4 rounded-md"
                            >
                                Back to Plans
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const appearance = { theme: "stripe" };
    const options = { clientSecret, appearance };

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
                {/* Stepper en la parte superior */}
                <div className="w-full max-w-2xl mx-auto mb-12">
                    <Stepper currentStep={3} />
                </div>

                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-gray-800 tracking-tight">
                        Secure Checkout
                    </h1>
                    <p className="mt-2 text-lg text-gray-500">
                        Complete your payment details below.
                    </p>
                </div>

                {/* Contenedor principal de dos columnas */}
                <div className="grid grid-cols-1 lg:grid-cols-2 lg:gap-16">

                    {/* --- Columna Derecha: Resumen del Pedido --- */}
                    <div className="bg-white rounded-xl shadow-lg p-6 lg:p-8 h-fit">
                        <h2 className="text-2xl font-semibold text-gray-800 border-b pb-4 mb-6">
                            Order Summary
                        </h2>

                        {planDetails ? (
                            <div className="space-y-4">
                                <div className="flex justify-between items-center text-gray-800">
                                    <span className="text-lg font-bold">{planDetails.name}</span>
                                    <span className="text-lg font-bold">${planDetails.price.toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between items-center text-gray-500 text-sm mb-4">
                                    <span>Billed Monthly</span>
                                    <span>USD</span>
                                </div>

                                {/* Mostrar plan actual vs nuevo plan */}
                                {currentUserPlan && (
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                                        <h4 className="font-semibold text-blue-800 mb-2">Upgrading from:</h4>
                                        <p className="text-blue-700 text-sm">{currentUserPlan.name} (${currentUserPlan.price}/month)</p>
                                    </div>
                                )}

                                {/* --- INICIO: Renderizado dinámico de las características del plan --- */}
                                <div className="border-t pt-6 space-y-3">
                                    <h3 className="text-md font-semibold text-gray-700">What's included:</h3>
                                    {planDetails.features && planDetails.features.map((featureGroup, index) => (
                                        <div key={index} className="pl-2">
                                            <p className="font-semibold text-gray-600">{featureGroup.title}</p>
                                            <ul className="mt-2 space-y-2">
                                                {featureGroup.features.map((feature, fIndex) => (
                                                    <li key={fIndex} className="flex items-start text-sm text-gray-500">
                                                        <CheckCircle size={16} className="mr-2 mt-0.5 text-green-500 flex-shrink-0" />
                                                        <span>{feature}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    ))}
                                </div>
                                {/* --- FIN: Renderizado dinámico --- */}

                                <div className="border-t pt-4 mt-4">
                                    <div className="flex justify-between items-center text-xl font-bold text-gray-900">
                                        <span>Total</span>
                                        <span>${planDetails.price.toFixed(2)}</span>
                                    </div>
                                </div>
                                <div className="mt-6 text-sm text-gray-500 flex items-center">
                                    <ShieldCheck size={16} className="mr-2 text-green-500" />
                                    <span>Secure payment powered by Stripe.</span>
                                </div>
                            </div>
                        ) : (
                            <p>Loading plan details...</p>
                        )}
                    </div>

                    {/* --- Columna Izquierda: Detalles de Pago --- */}
                    <div className="mb-12">
                        {clientSecret && (
                            <Elements options={options} stripe={stripePromise}>
                                <UpgradeCheckoutForm clientSecret={clientSecret} />
                            </Elements>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UpgradeCheckoutPage;
